import { CalendarDays } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';

import type { ShellContext } from '@/components/layout/AppShell';
import { TaskGroup, TaskList } from '@/components/tasks/TaskList';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { useUpcoming } from '@/hooks/useTasks';
import { formatUpcomingHeading } from '@/lib/dates';
import { useT } from '@/lib/i18n';
import { Page } from '@/routes/Today';
import { useTaskActions } from '@/routes/useTaskActions';

export function UpcomingRoute() {
  const t = useT();
  const { currentUser, openTask } = useOutletContext<ShellContext>();
  const { data, isPending, error, refetch } = useUpcoming();
  const actions = useTaskActions(openTask);

  if (isPending)
    return (
      <Page>
        <LoadingState />
      </Page>
    );
  if (error)
    return (
      <Page>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Page>
    );
  if (!data) return null;

  return (
    <Page>
      <h1 className="mb-5 text-xl font-semibold tracking-tight">{t('nav.upcoming')}</h1>

      {data.groups.length === 0 ? (
        <EmptyState
          icon={<CalendarDays className="h-5 w-5" />}
          title={t('upcoming.empty.title')}
          hint={t('upcoming.empty.hint')}
        />
      ) : (
        data.groups.map((group) => (
          <TaskGroup
            key={group.date}
            title={formatUpcomingHeading(group.date, data.reference_date)}
            count={group.tasks.length}
          >
            <TaskList
              tasks={group.tasks}
              today={data.reference_date}
              currentUserId={currentUser.id}
              actions={actions}
            />
          </TaskGroup>
        ))
      )}
    </Page>
  );
}
